import { useCallback, useEffect, useRef } from 'react';

export type UseFullscreenAutoHideParams = {
  isFullscreen: boolean;
  containerRef: React.RefObject<HTMLElement>;
  childrenRefs: React.RefObject<HTMLElement>[];
  timeoutMs?: number;
};

export const useFullscreenAutoHide = ({
  isFullscreen,
  containerRef,
  childrenRefs,
  timeoutMs,
}: UseFullscreenAutoHideParams) => {
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isActiveRef = useRef(false);

  const hideChildren = useCallback(() => {
    if (!isFullscreen) return;

    childrenRefs.forEach((ref) => {
      if (ref.current) {
        ref.current.style.opacity = '0';
        ref.current.style.pointerEvents = 'none';
        ref.current.style.visibility = 'hidden';
      }
    });
    isActiveRef.current = false;
  }, [isFullscreen, childrenRefs]);

  const showChildren = useCallback(() => {
    childrenRefs.forEach((ref) => {
      if (ref.current) {
        ref.current.style.opacity = '1';
        ref.current.style.pointerEvents = 'all';
        ref.current.style.visibility = 'visible';
      }
    });
    isActiveRef.current = true;
  }, [childrenRefs]);

  const startInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    if (isFullscreen) {
      inactivityTimerRef.current = setTimeout(() => {
        hideChildren();
      }, timeoutMs);
    }
  }, [isFullscreen, timeoutMs, hideChildren]);

  const resetInactivityTimer = useCallback(() => {
    if (!isFullscreen) return;
    showChildren();
    startInactivityTimer();
  }, [isFullscreen, showChildren, startInactivityTimer]);

  // biome-ignore lint/correctness/useExhaustiveDependencies(resetInactivityTimer): Only depend on isFullScreen to prevent constant re-creation of event listeners
  // biome-ignore lint/correctness/useExhaustiveDependencies(isFullscreen): Only depend on isFullScreen to prevent constant re-creation of event listeners
  // biome-ignore lint/correctness/useExhaustiveDependencies(containerRef.current): Only depend on isFullScreen to prevent constant re-creation of event listeners
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMouseMove = resetInactivityTimer;
    const handleMouseEnter = resetInactivityTimer;
    const handleClick = resetInactivityTimer;

    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseenter', handleMouseEnter);
    container.addEventListener('click', handleClick);

    return () => {
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseenter', handleMouseEnter);
      container.removeEventListener('click', handleClick);
    };
  }, [isFullscreen]);

  // biome-ignore lint/correctness/useExhaustiveDependencies(showChildren): Only depend on isFullScreen to prevent constant re-creation of event listeners
  // biome-ignore lint/correctness/useExhaustiveDependencies(resetInactivityTimer): Only depend on isFullScreen to prevent constant re-creation of event listeners
  useEffect(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }

    if (isFullscreen) {
      resetInactivityTimer();
    } else {
      showChildren();
    }
  }, [isFullscreen]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, []);
};
